import clsx from "clsx";

import {
  formatValue,
  isDigitalSignatureField,
  ResponseDetails,
  UploadedFile,
} from "@smart/bridge-types-basic";
import {
  Avatar,
  Displayer,
  Icon,
  RequiredAsterisk,
  Tooltip,
} from "@smart/itops-sb-design-system-dom";
import { specialChars } from "@smart/itops-utils-basic";

import { SummaryAppointment } from "./summary-appointment";
import { SummaryPayment } from "./summary-payment";
import { SummarySignature } from "./summary-signature";
import {
  FieldComponentType,
  fieldFallbackLabel,
  FieldItem,
  syncStatusColour,
  syncStatusLabel,
} from "../../types";

export type SummaryFieldProps = {
  field: FieldItem;
  value: any;
  index?: number;
  responsesDetails?: ResponseDetails[];
  selectedLanguage?: string;
};

export const SummaryField = ({
  field,
  value,
  index,
  responsesDetails,
  selectedLanguage,
}: SummaryFieldProps) => {
  const { type, options, label, mandatory, field: layoutField } = field;

  const displayFieldValue = () => {
    if (type === "file" && Array.isArray(value)) {
      const uploadedFiles = value?.filter(
        (file: UploadedFile) => file.uploadStatus === "uploaded",
      );
      const dataTestId = `file-field-item-${field.uri}`;
      return uploadedFiles?.length ? (
        uploadedFiles.map((file: UploadedFile) => (
          <p
            key={file.key || file.fileName}
            className="text-ui"
            data-testid={dataTestId}
          >
            {file.fileName}
          </p>
        ))
      ) : (
        <p className="text-ui" data-testid={dataTestId}>
          {specialChars.enDash}
        </p>
      );
    }

    if (type === "payment")
      return <SummaryPayment response={value} fieldUri={field.uri} />;

    if (type === "multilineText") {
      const paragraphs = formatValue(value, type, options)
        ?.trim()
        .split("\n\n")
        .filter(Boolean);

      return paragraphs?.length ? (
        paragraphs.map((text) => (
          <p key={text} className="text-ui">
            {text}
          </p>
        ))
      ) : (
        <p className="text-ui">
          <span>{specialChars.enDash}</span>
        </p>
      );
    }

    if (type === "checkbox" || type === "choice") {
      const values = Array.isArray(value) ? value : [value];
      const filteredValues = values.filter(Boolean);

      const getValue = (v: string) => {
        const valueInOptions = options?.find((o) => o.value === v);
        if (valueInOptions) {
          return valueInOptions.label;
        }
        // There is no "Other" options for field with dataSourceMapping
        return layoutField?.dataSourceMapping ? v : `Other - ${v}`;
      };

      return !filteredValues.length ? (
        <p className="text-ui">
          <span>{specialChars.enDash}</span>
        </p>
      ) : (
        filteredValues.map((v) => (
          <p key={v} className="text-ui">
            {getValue(v)}
          </p>
        ))
      );
    }

    if (type === "signature")
      return (
        <SummarySignature
          response={value}
          isDigitalSignature={isDigitalSignatureField(field)}
          fieldUri={field.uri}
          selectedLanguage={selectedLanguage}
        />
      );

    const formatted = formatValue(value, type, options).trim();
    return (
      <div className="text-ui">
        <span>{formatted || specialChars.enDash}</span>
      </div>
    );
  };

  const isInvalidPaymentField =
    type === "payment" &&
    (!value || !field.payment?.amountInCents || !field.payment.bankAccount);

  if (isInvalidPaymentField) return null;

  if (type === "info") {
    if (!field.includeInSummary) return null;
    return (
      <div
        key={index === undefined ? field.uri : `${field.uri}-${index}`}
        className="py-[12px] print:break-inside-avoid text-paragraph"
        data-testid={`summary-field-wrapper-${field.uri}`}
      >
        <Displayer
          value={label}
          fallback={fieldFallbackLabel[type as FieldComponentType]}
        />
      </div>
    );
  }

  if (type === "appointment") {
    return (
      <SummaryAppointment
        field={field}
        response={value}
        responseDetails={responsesDetails?.find(
          (r) => r.fieldUri === field.uri,
        )}
        selectedLanguage={selectedLanguage}
      />
    );
  }

  const { syncStatus, info } =
    responsesDetails?.find((r) => r.fieldUri === field.uri) || {};

  return (
    <div
      key={index === undefined ? field.uri : `${field.uri}-${index}`}
      className="flex flex-col gap-small print:break-inside-avoid hyphens-auto"
      data-testid={`summary-field-wrapper-${field.uri}`}
    >
      <div className="flex flex-col gap-xSmall">
        <div className="[&_h3]:text-ui [&_h3]:font-semibold flex items-center gap-small">
          <div>
            <Displayer
              paragraphTag="h3"
              value={label}
              fallback={fieldFallbackLabel[type as FieldComponentType]}
              hintElement={mandatory && <RequiredAsterisk />}
            />
          </div>
          {responsesDetails?.length && (
            <div className="flex items-center gap-small">
              {field.layout && (
                <Tooltip
                  text={`${field.layout?.displayName} - ${field.field?.name}`}
                  offsetBy={6}
                >
                  <Avatar
                    icon="solidNetworkWired"
                    bgColor="bg-orange-200"
                    size={20}
                  />
                </Tooltip>
              )}
              {syncStatus && (
                <Tooltip
                  text={`${syncStatusLabel[syncStatus]}${info ? ` - ${info}` : ""}`}
                  offsetBy={6}
                >
                  <Icon
                    name="solidCircleInfo"
                    className={clsx(
                      "flex items-center",
                      syncStatusColour[syncStatus],
                    )}
                  />
                </Tooltip>
              )}
            </div>
          )}
        </div>
      </div>
      {displayFieldValue()}
    </div>
  );
};
